// gallix-api.service.ts
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {Auth, idToken} from '@angular/fire/auth';
import {logConsoleError} from '../../shared-module-ui/utils/logger/logger';
import {environment} from '../../../../environments/environment';

/** ---------- API contracts ---------- */
export interface IStoreStockCodeRule {
  digits: number;
  prefix: string;
  storeId: string;
  suffix: string;
}

export type INextStockCodesResponse = Record<string, string>;

interface IGenerateNextStockCodeRequest {
  // snake_case per OpenAPI
  store_rules: IStoreStockCodeRule[];
}

interface IGenerateNextStockCodeApiResponse {
  codes: INextStockCodesResponse;
}

export interface IProductMappingLink {
  fromCode: string;
  fromStoreId: string;
  toCode: string;
  toStoreId: string;
}

export interface IProductMappingBatchRequest {
  links: IProductMappingLink[];
  sessionId: string;
}

export interface IProductMappingMember {
  code: string;
  storeId: string;
}

export interface IProductMappingConflict {
  keptCode: string;
  reason: string;
  rejectedCode: string;
  storeId: string;
}

export interface IProductMappingBatchResponse {
  conflicts: IProductMappingConflict[];
  genCode: string;
  memberStoreCountTotal: number;
  memberStoreCountVisible: number;
  members: IProductMappingMember[];
  mergedFrom: string[];
}

export interface IProductMappingGroupResponse {
  genCode: string;
  memberStoreCountTotal: number;
  memberStoreCountVisible: number;
  members: IProductMappingMember[];
}

export interface IProductMappingLookupResponse {
  code: string;
  genCode: string | null;
  memberStoreCountTotal: number;
  memberStoreCountVisible: number;
  storeId: string;
}

export interface IProductMappingMergeGroupsRequest {
  fromGenCode: string;
  toGenCode: string;
  bridgeCode?: string;
  bridgeStoreId?: string;
}

/** ---------- Service ---------- */
@Injectable({providedIn: 'root'})
export class GallixApiService {
  private readonly apiUrl = environment.gallixApi;

  constructor(private http: HttpClient, private auth: Auth) {
  }

  /**
   * Generate the next stock codes for one or more stores.
   * Returns a map keyed by storeId → next code.
   */
  generateNextStockCode(
    storeRules: IStoreStockCodeRule[],
  ): Observable<INextStockCodesResponse> {
    const url = `${this.apiUrl}/bigquery/generate-next-stock-code`;
    const body: IGenerateNextStockCodeRequest = {store_rules: storeRules};

    return idToken(this.auth).pipe(
      take(1),
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No Firebase user'));
        const headers = new HttpHeaders({Authorization: `Bearer ${token}`});
        return this.http.post<IGenerateNextStockCodeApiResponse>(url, body, {headers});
      }),
      map((res) => res?.codes ?? {}),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error)),
    );
  }

  /**
   * Persist confirmed product mapping links and merge islands.
   */
  saveProductMappingBatch(body: IProductMappingBatchRequest): Observable<IProductMappingBatchResponse> {
    const url = `${this.apiUrl}/product-mapping/batch`;
    return idToken(this.auth).pipe(
      take(1),
      switchMap((token: string | null) => {
        if (!token) {
          return throwError(() => new Error('No Firebase user'));
        }
        return this.http.post<IProductMappingBatchResponse>(url, body, {
          headers: new HttpHeaders({Authorization: `Bearer ${token}`}),
        });
      }),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error)),
    );
  }

  /**
   * Load ACL-filtered members of a mapping group.
   */
  getProductMappingGroup(genCode: string): Observable<IProductMappingGroupResponse> {
    const url = `${this.apiUrl}/product-mapping/groups/${encodeURIComponent(genCode)}`;
    return idToken(this.auth).pipe(
      take(1),
      switchMap((token: string | null) => {
        if (!token) {
          return throwError(() => new Error('No Firebase user'));
        }
        return this.http.get<IProductMappingGroupResponse>(url, {
          headers: new HttpHeaders({Authorization: `Bearer ${token}`}),
        });
      }),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error)),
    );
  }

  /**
   * Resolve whether a store SKU is already mapped.
   */
  lookupProductMapping(storeId: string, code: string): Observable<IProductMappingLookupResponse> {
    const url = `${this.apiUrl}/product-mapping/lookup`;
    const params = new HttpParams().set('store_id', storeId).set('code', code);
    return idToken(this.auth).pipe(
      take(1),
      switchMap((token: string | null) => {
        if (!token) {
          return throwError(() => new Error('No Firebase user'));
        }
        return this.http.get<IProductMappingLookupResponse>(url, {
          headers: new HttpHeaders({Authorization: `Bearer ${token}`}),
          params,
        });
      }),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error)),
    );
  }

  /**
   * Explicit island merge between two gen codes.
   */
  mergeProductMappingGroups(body: IProductMappingMergeGroupsRequest): Observable<IProductMappingBatchResponse> {
    const url = `${this.apiUrl}/product-mapping/merge-groups`;
    return idToken(this.auth).pipe(
      take(1),
      switchMap((token: string | null) => {
        if (!token) {
          return throwError(() => new Error('No Firebase user'));
        }
        return this.http.post<IProductMappingBatchResponse>(url, body, {
          headers: new HttpHeaders({Authorization: `Bearer ${token}`}),
        });
      }),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error)),
    );
  }

  /** Centralised error translator with FastAPI detail passthrough */
  private handleHttpError(err: HttpErrorResponse): Observable<never> {
    // Prefer precise FastAPI validation messages when present
    const detail = (err.error?.detail && Array.isArray(err.error.detail)) ? err.error.detail : null;
    const message =
      detail?.map((d) => `${d.msg}${d.loc ? ` @ ${d.loc.join(' > ')}` : ''}`).join('; ') ||
      err.error?.message ||
      `HTTP ${err.status}: ${err.statusText || 'Unexpected server error'}`;

    logConsoleError('Api Error', err);
    return throwError(() => new Error(message));
  }
}
